import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Select,
  Text,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  Card,
  CardBody,
  Grid,
  GridItem,
  VStack,
  HStack,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  Flex,
  Badge,
  Button,
} from '@chakra-ui/react';
import Chart from 'react-apexcharts';
import axios from 'axios';
import axiosClient from '../../axios-client';
import * as XLSX from 'xlsx';
import { DownloadIcon } from '@chakra-ui/icons';

const SubscriptionStats = () => {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [selectedMonth, setSelectedMonth] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statsData, setStatsData] = useState({
    globalStats: {
      months: [],
      annualSubscriptions: [],
      monthlySubscriptions: [],
      revenue: [],
      totalSubscriptions: null,
      totalAnnual: null,
      totalMonthly: null
    },
    monthDetail: null,
    availableYears: [],
    users: []
  });

  const months = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  // Function to convert month names between English and French
  const monthNameConverter = {
    'January': 'Janvier',
    'February': 'Février',
    'March': 'Mars',
    'April': 'Avril',
    'May': 'Mai',
    'June': 'Juin',
    'July': 'Juillet',
    'August': 'Août',
    'September': 'Septembre',
    'October': 'Octobre',
    'November': 'Novembre',
    'December': 'Décembre',
    'Janvier': 'January',
    'Février': 'February',
    'Mars': 'March',
    'Avril': 'April',
    'Mai': 'May',
    'Juin': 'June',
    'Juillet': 'July',
    'Août': 'August',
    'Septembre': 'September',
    'Octobre': 'October',
    'Novembre': 'November',
    'Décembre': 'December'
  };

  // Fetch data from the API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Build query parameters
        let url = `subscription-stats/?year=${selectedYear}`;
        if (selectedMonth) {
          // Convert French month name to English for the API
          const englishMonth = monthNameConverter[selectedMonth];
          url += `&month=${englishMonth}`;
        }

        const response = await axiosClient.get(url);

        // Update data state with response from API
        setStatsData(response.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching subscription stats:', err);
        setError('Une erreur est survenue lors du chargement des données.');
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedYear, selectedMonth]);

  // Reset month selection when year changes
  useEffect(() => {
    setSelectedMonth('');
  }, [selectedYear]);

  // Convert English month names in the response to French for display
  const displayMonths = statsData.globalStats.months.map(
    month => monthNameConverter[month] || month
  );

  const globalChartData = {
    series: [
      {
        name: 'Type abonnements annuels',
        data: statsData.globalStats.annualSubscriptions,
      },
      {
        name: 'Type abonnements mensuels',
        data: statsData.globalStats.monthlySubscriptions,
      },
    ],
    options: {
      chart: { id: 'global-subscription-chart' },
      xaxis: { categories: displayMonths },
      colors: ['#fa8d51', '#6ad7d9'],
      export: {
        csv: {
          delimiter: ',', // <-- This is the key change
        },
      },
    },
  };

  const monthlyChartData = statsData.monthDetail ? {
    series: [
      {
        name: 'Type abonnements annuels',
        data: [statsData.monthDetail.annualSubscriptions],
      },
      {
        name: 'Type abonnements mensuels',
        data: [statsData.monthDetail.monthlySubscriptions],
      },
    ],
    options: {
      chart: {
        id: 'monthly-subscription-chart',
        toolbar: {
          export: {
            csv: {
              columnDelimiter: ',', // Ensure comma delimiter
              dateFormatter(timestamp) {
                return new Date(timestamp).toLocaleDateString();
              },
              formatter(seriesName, opts) {
                // opts.dataPointIndex: index of data in the series
                // opts.w.config.series: full series array
                const index = opts.dataPointIndex;
                const row = opts.w.config.series.map(s => s.data[index]);
                const category = opts.w.config.xaxis.categories[index];
                return [category, ...row].join(',');
              }
            }
          }
        }
      },
      xaxis: { categories: [selectedMonth] },
      colors: ['#fa8d51', '#6ad7d9'],
      export: {
        csv: {
          delimiter: ',', // <-- This is the key change
        },
      },
    },
  } : null;

  // Format the revenue as Euro currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const exportToExcel = () => {
    // Prepare data for export
    let worksheetData = [];

    // Add year info
    worksheetData.push([`Statistiques d'abonnement pour ${selectedYear}`]);
    worksheetData.push([]);  // Empty row for spacing

    // Add header row
    worksheetData.push(['Mois', 'Abonnements annuels', 'Abonnements mensuels', 'Total']);

    // Add data rows for each month
    for (let i = 0; i < displayMonths.length; i++) {
      const annualSubs = statsData.globalStats.annualSubscriptions[i] || 0;
      const monthlySubs = statsData.globalStats.monthlySubscriptions[i] || 0;
      worksheetData.push([
        displayMonths[i],
        annualSubs,
        monthlySubs,
        annualSubs + monthlySubs
      ]);
    }

    // Add total row
    const totalAnnual = statsData.globalStats.annualSubscriptions.reduce((a, b) => a + b, 0);
    const totalMonthly = statsData.globalStats.monthlySubscriptions.reduce((a, b) => a + b, 0);
    worksheetData.push([
      'Total',
      totalAnnual,
      totalMonthly,
      totalAnnual + totalMonthly
    ]);
    worksheetData.push([]);
    worksheetData.push(['Liste des utilisateurs']);
    worksheetData.push(['Nom', 'Prénom', 'Email', 'Téléphone', 'Nombre de paiements']);

    statsData.users.forEach(user => {
      worksheetData.push([
        // user.id,
        user.last_name,
        user.first_name,
        user.email,
        user.tel,
        user.payment_count,
      ]);
    });
    // Add monthly detail if available
    if (selectedMonth && statsData.monthDetail) {
      worksheetData.push([]);  // Empty row for spacing
      worksheetData.push([`Détail pour ${selectedMonth} ${selectedYear}`]);
      worksheetData.push([]);  // Empty row for spacing
      worksheetData.push(['Type', 'Nombre']);
      worksheetData.push(['Abonnements annuels', statsData.monthDetail.annualSubscriptions]);
      worksheetData.push(['Abonnements mensuels', statsData.monthDetail.monthlySubscriptions]);
      worksheetData.push(['Total des abonnements', statsData.monthDetail.totalSubscriptions]);
      worksheetData.push([]);  // Empty row for spacing
      worksheetData.push(['Chiffre d\'affaires', statsData.monthDetail.totalRevenue]);

      worksheetData.push([]);
      worksheetData.push(['Liste des utilisateurs']);
      worksheetData.push([ 'Nom', 'Prénom', 'Email', 'Téléphone']);

      statsData.monthDetail.users.forEach(user => {
        worksheetData.push([
          // user.id,
          user.last_name,
          user.first_name,
          user.email,
          user.tel,
        
        ]);
      });
      worksheetData.push([]);
    }


    // Create workbook and worksheet
    const ws = XLSX.utils.aoa_to_sheet(worksheetData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Statistiques");

    // Generate Excel file
    const fileName = selectedMonth
      ? `Statistiques_Abonnements_${selectedMonth}_${selectedYear}.xlsx`
      : `Statistiques_Abonnements_${selectedYear}.xlsx`;

    XLSX.writeFile(wb, fileName);
  };


  if (loading) {
    return (
      <Center h="300px">
        <Spinner size="xl" />
      </Center>
    );
  }

  if (error) {
    return (
      <Alert status="error">
        <AlertIcon />
        {error}
      </Alert>
    );
  }


  return (
    <Box p={{ base: 0, md: 6 }}>
      <Heading size={"lg"} mb={5}>Statistiques d'abonnement</Heading>

      <Box my={6}>

        <Flex
          direction={{ base: "column", lg: "row" }}
          gap={4}
        >
          {/* Selects container - always in same row */}
          <Flex gap={4} flex={{ md: 4 }}>
            <Select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              flex="1"
            >
              {statsData.availableYears.map((year) => (
                <option key={year} value={year}>{year}</option>
              ))}
            </Select>

            <Select
              placeholder="Sélectionnez un mois"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              flex="1"
            >
              {months.map((month, index) => (
                <option key={index} value={month}>{month}</option>
              ))}
            </Select>
          </Flex>

          {/* Button */}
          <Button
            leftIcon={<DownloadIcon />}
            colorScheme="teal"
            onClick={exportToExcel}
            size="md"
            width={{ base: '100%', md: 'auto' }}
            mt={{ base: 2, md: 0 }}
            flex={{ base: 'none', lg: 1 }}
          >
            Exporter en Excel
          </Button>
        </Flex>
      </Box>

      <Box my={10}>
        <Flex mb={5} align={"center"} direction={{ base: "column", md: "row" }} justify={"space-between"}>
          <Text fontSize="xl" fontWeight="bold" mb={2}>Vue globale ({selectedYear})</Text>
          <Text fontSize="md" fontWeight="bold" mb={2}>Total des abonnements actuels : <Badge colorScheme='purple' fontSize={"lg"} px={2} mx={2}> {statsData.globalStats.totalSubscriptions} </Badge> </Text>
        </Flex>

        <Chart options={globalChartData.options} series={globalChartData.series} type="line" height={300} />
      </Box>

      {selectedMonth && statsData.monthDetail && (
        <Box mb={5}>
          <Text fontSize="xl" fontWeight="bold" mb={4}>Détail du mois : {selectedMonth}</Text>
          <Grid templateColumns={{ base: '1fr', md: '2fr 1fr' }} gap={6}>
            <GridItem>
              <Chart options={monthlyChartData.options} series={monthlyChartData.series} type="bar" height={250} />
            </GridItem>
            <GridItem>
              <VStack spacing={4} align="stretch">
                <Card boxShadow={"md"}>
                  <CardBody>
                    <Stat>
                      <StatLabel>Chiffre d'affaires</StatLabel>
                      <StatNumber>{formatCurrency(statsData.monthDetail.totalRevenue)}</StatNumber>
                    </Stat>
                  </CardBody>
                </Card>
                <Card boxShadow={"md"}>
                  <CardBody>
                    <Stat>
                      <StatLabel>Total des abonnements</StatLabel>
                      <StatNumber>{statsData.monthDetail.totalSubscriptions}</StatNumber>
                    </Stat>
                  </CardBody>
                </Card>
                {/* <Card>
                  <CardBody>
                    <Stat>
                      <StatLabel>Répartition des coupons</StatLabel>
                      <HStack spacing={6} mt={2}>
                        <Box>
                          <Text fontSize="sm" color="gray.600">Avec coupon</Text>
                          <Text fontSize="lg" fontWeight="bold">{statsData.monthDetail.withPromoCode}</Text>
                        </Box>
                        <Box>
                          <Text fontSize="sm" color="gray.600">Sans coupon</Text>
                          <Text fontSize="lg" fontWeight="bold">{statsData.monthDetail.withoutPromoCode}</Text>
                        </Box>
                      </HStack>
                    </Stat>
                  </CardBody>
                </Card> */}
              </VStack>
            </GridItem>
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default SubscriptionStats;